import { VoxcoEventTypes, VoxcoEvent } from "./events";
import { StoreState } from "../models/states/StoreState";
import { __evaluateSafe__ } from "../expressions/parser";
import { Log } from "../utils/logging";
import { ValidationOptionsModel } from "../models/ValidationOptionsModel";
import { MentionBuilderDelegate } from "../models/builders/MentionBuilder";
import { DispositionCode } from "../models/enums/DispositionCode";
import { Piping } from "../piping";
import * as ACTIONS from "../redux/actions";
import { createRespondent } from "../models/factories/respondentFactory";
import { createQuestionnaire, createTheme } from "../models/factories/questionnaireFactory";
import { ThemeModel } from "../models/ThemeModel";
import { DispatchType, StoreType } from "../redux/actions/types";
import { BranchToOptions } from "../models/BranchToOptions";
import { convertToSurveyMode } from "../models/enums/SurveyMode";
import { getRandomInt } from "../randomization/Randomizer";
import { applyCSS, generateCSS } from "../utils/dynamic-css/coreDynamicCss";

export type VoxcoEventListener<E extends VoxcoEvent> = (this: PublicApi, ev: E) => void;

export interface VoxcoEventListeners {
    [event: string]: VoxcoEventListener<any>[];
}

export interface VoxcoConfig {
    debug: boolean;
    mode: "test" | "preview" | "normal" | "simulation";
    displayInThemeEditor: boolean;
    messages: boolean;
    mobileMode: boolean;
    apiUrl?: string;
    survey?: any;
    containerHost?: string;
    previewQuestionId?: number;
    hideAccessibilityPanel: boolean;
}

const tricklingListeners: VoxcoEventListeners = {};
const capturingListeners: VoxcoEventListeners = {};

const configDefaults: VoxcoConfig = {
    debug: false,
    mode: "normal",
    messages: false,
    mobileMode: false,
    displayInThemeEditor: false,
    hideAccessibilityPanel: false,
};

const simulatedRespondent = createRespondent({
    respondentId: 0,
    simulated: true,
    isNew: true,
    seed: getRandomInt(),
    sessionStartTime: 0,
    offlineUserId: 0,
    offlineUserName: "",
});

const simulatedDevice = {
    model: "simulated",
    browser: "simulated",
    os: "simulated",
    version: "simulated",
    type: "simulated",
};

function getListeners<K extends keyof VoxcoEventTypes>(
    event: K,
    capture?: boolean,
): VoxcoEventListener<any>[] {
    const listeners = capture ? capturingListeners : tricklingListeners;
    if (!listeners[event]) {
        listeners[event] = [];
    }
    return listeners[event];
}

function createVoxcoEvent(store: StoreType, e: VoxcoEvent): VoxcoEvent {
    const result = {
        ...e,
        interview: store.getState().interview,
        defaultPrevented: false,
        stoppedPropagation: false,
        stoppedImmediatePropagation: false,
    };
    result.preventDefault = () => {
        result.defaultPrevented = true;
    };
    result.stopPropagation = () => {
        result.stoppedPropagation = true;
    };
    result.stopImmediatePropagation = () => {
        result.stoppedImmediatePropagation = true;
    };
    return result;
}

export class PublicApi {
    static TAG = "publicApi";

    version = require("../../package.json").version;
    lib = process.env.LIB_NAME;
    store: StoreType = undefined;
    dispatch: DispatchType = undefined;

    async init(config: VoxcoConfig = configDefaults) {
        const {
            mode,
            debug,
            messages,
            apiUrl,
            survey,
            containerHost,
            previewQuestionId,
            hideAccessibilityPanel,
        } = config;
        this.dispatch(ACTIONS.setPageMessagesEnabled(messages));
        if (debug) {
            this.dispatch(ACTIONS.debugEnabled());
        }

        this.dispatch(ACTIONS.setHideAccessibilityPanel(hideAccessibilityPanel));
        this.dispatch(ACTIONS.setSurveyMode(convertToSurveyMode(mode)));

        // Convert the displayInThemeEditor flag to boolean before setting it
        // This is because the parameter sent from the backend would always get stored as string
        const displayInThemeEditor =
            config.displayInThemeEditor &&
            config.displayInThemeEditor.toString().toLowerCase() === "true";
        this.dispatch(ACTIONS.setDisplayInThemeEditor(displayInThemeEditor));

        if (apiUrl !== undefined) {
            this.dispatch(ACTIONS.connectionStart(apiUrl));
        } else if (survey !== undefined) {
            this.dispatch(ACTIONS.respondentChanged(simulatedRespondent));
            this.dispatch(ACTIONS.deviceChanged(simulatedDevice));
            this.dispatch(ACTIONS.questionnaireChanged(createQuestionnaire(survey)));
            this.dispatch(ACTIONS.connectionConnected("simulated"));
        }

        if (previewQuestionId && previewQuestionId > -1) {
            Voxco.once("pageReady", () =>
                this.dispatch(ACTIONS.navigationPreviewQuestion(previewQuestionId)),
            );
        }

        // Alert host window when we are ready or quitting.
        if (containerHost && window.parent) {
            window.parent.postMessage({ type: "ready" }, containerHost);

            Voxco.on("quit", ({ dispositionCode, options }) => {
                window.parent.postMessage(
                    { type: "quit", dispositionCode, options },
                    containerHost,
                );
            });
        }

        // Listen for page messages.
        window.addEventListener("message", (event) => {
            const { origin, data } = event;
            if (containerHost !== origin) {
                return;
            }
            switch (data.type) {
                case "previewTheme": {
                    const theme = createTheme(data.theme);
                    if (data.isDynamicCssChanged && theme.settings?.DynamicCss) {
                        const css = generateCSS(theme.settings, theme.settings.DynamicCss);
                        applyCSS(css);
                    } else {
                        this.previewTheme(theme);
                    }
                    break;
                }
                default:
                    break;
            }
        });
    }

    log = (message?: any) => Log.d(PublicApi.TAG, "LOG", message);
    error = (error?: any) =>
        Log.e(PublicApi.TAG, "ERROR", "An error has occurred in custom.js.", error);

    on<K extends keyof VoxcoEventTypes>(
        event: K,
        listener: VoxcoEventListener<VoxcoEventTypes[K]>,
        capture?: boolean,
    ) {
        const listeners = getListeners(event, capture);
        listeners.push(listener);
        if (event === "pageReady") {
            // If the page is ready, call right away.
            const store: StoreState = this.store.getState();
            if (
                store.interview.navigation.currentPage !== undefined &&
                store.interview.navigation.currentPage.isReady
            ) {
                listener.apply(this, [store.interview.navigation.currentPage]);
            }
        }
    }

    off<K extends keyof VoxcoEventTypes>(
        event: K,
        listener: VoxcoEventListener<VoxcoEventTypes[K]>,
        capture?: boolean,
    ) {
        const listeners = getListeners(event, capture);
        const index = listeners.indexOf(listener);
        if (index > -1) listeners.splice(index, 1);
    }

    once<K extends keyof VoxcoEventTypes>(
        event: K,
        listener: VoxcoEventListener<VoxcoEventTypes[K]>,
        capture?: boolean,
    ) {
        const api = this;
        let done = false;
        const listenerWrapper = (ev: VoxcoEventTypes[K]) => {
            if (done) return;
            done = true;
            const result = listener.apply(api, [ev]);
            this.off(event, listenerWrapper, capture);
            return result;
        };
        this.on(event, listenerWrapper, capture);
    }

    trigger<K extends keyof VoxcoEventTypes>(event: K, data?: VoxcoEventTypes[K]): any {
        let evt = createVoxcoEvent(this.store, data);

        const capturingListeners = getListeners(event, true);
        for (const listener of capturingListeners) {
            try {
                evt = listener.apply(this, [evt]) || evt;
            } catch (ex) {
                this.error(ex);
            }
            if (evt.stoppedPropagation) {
                return evt;
            }
            if (evt.stoppedImmediatePropagation) {
                break;
            }
            evt = { ...evt, stoppedImmediatePropagation: false };
        }

        const listeners = [...getListeners(event)];

        let listener;
        for (let i = 0; i < listeners.length; i++) {
            listener = listeners[i];
            try {
                evt = listener.apply(this, [evt]) || evt;
                if (event === "questionReady") {
                    this.off(event, listener);
                }
            } catch (ex) {
                this.error(ex);
            }
            if (evt.stoppedPropagation) {
                return evt;
            }
            if (evt.stoppedImmediatePropagation) {
                break;
            }
            evt = { ...evt, stoppedImmediatePropagation: false };
        }

        return evt;
    }

    previewTheme(theme: ThemeModel) {
        this.dispatch(
            ACTIONS.previewTheme({
                ...theme,
                settings: {
                    ...theme.settings,
                    CustomJavascript: undefined,
                },
            }),
        );
    }

    evaluate = (expression: string, simple?: boolean) =>
        __evaluateSafe__(this.store.getState().interview, expression, simple);

    recall = (text: string) => Piping.evaluateRecalls(this.store.getState().interview, text);

    showDebugInfo = () => this.dispatch(ACTIONS.debugInfoEnabled());

    onPageReady = (listener: VoxcoEventListener<VoxcoEventTypes["pageReady"]>) =>
        this.on("pageReady", listener);

    onQuestionReady = (listener: VoxcoEventListener<VoxcoEventTypes["questionReady"]>) => {
        this.on("questionReady", listener);
    };

    validatePage = (options?: ValidationOptionsModel) => this.trigger("validatePage", { options });
    onValidatePage = (listener: VoxcoEventListener<VoxcoEventTypes["validatePage"]>) =>
        this.on("validatePage", listener);

    setVariableMentions = (
        mentions: MentionBuilderDelegate | string | string[],
        variableName: string,
        matrixIndex?: number,
    ) => {
        if (typeof mentions === "function")
            return this.trigger("setVariableMentions", {
                builder: mentions,
                variableName,
                matrixIndex,
            });
        return this.trigger("setVariableMentions", {
            builder: (m) => m.clear().add(mentions as any),
            variableName,
            matrixIndex,
        });
    };

    onSetVariableMentions = (
        listener: VoxcoEventListener<VoxcoEventTypes["setVariableMentions"]>,
    ) => this.on("setVariableMentions", listener);

    branchTo = (options?: BranchToOptions) => this.trigger("branchTo", options);
    onBranchTo = (listener: VoxcoEventListener<VoxcoEventTypes["branchTo"]>) =>
        this.on("branchTo", listener);

    displayMessage = (message: string) => this.trigger("displayMessage", { message });

    nextPage = () => this.trigger("nextPage");
    onNextPage = (listener: VoxcoEventListener<VoxcoEventTypes["nextPage"]>) =>
        this.on("nextPage", listener);

    previousPage = () => this.trigger("previousPage");
    onPreviousPage = (listener: VoxcoEventListener<VoxcoEventTypes["previousPage"]>) =>
        this.on("previousPage", listener);

    quit = (
        dispositionCode?: DispositionCode,
        options?: {
            message?: string;
            closedStrata?: boolean;
            redirectUrl?: string;
        },
    ) => this.trigger("quit", { dispositionCode, options });

    onQuit = (listener: VoxcoEventListener<VoxcoEventTypes["quit"]>) => this.on("quit", listener);

    setAppointment = (appointment: Date) => this.trigger("setAppointment", { appointment });
    setEmail = (email: string) => this.trigger("setEmail", { email });
    setPhone = (phone: string) => this.trigger("setPhone", { phone });
    setTimeZone = (timeZone: string) => this.trigger("setTimeZone", { timeZone });
    setLoopRow = (blockName: string, row: number) => this.trigger("setLoopRow", { blockName, row });
}

export const Voxco = new PublicApi();
